import * as React from 'react';

import { getDisplayName } from 'components/hoc';
import { CardTypes } from 'defs/card-types';

import { getVirtualPageUrl, push as dataLayerPush } from 'helper/ga';

import { trackCardCompletedAction } from 'defs/ga/track/card';
import { trackCardTrackingComplete, trackCardTrackingOpen } from 'defs/ga/track/card-tracking';
import { trackProductCardOpen } from 'defs/ga/track/product';
import { trackProductCardWidgetAction } from 'defs/ga/track/product';

import { CardTypeTypes } from 'types/Card';

export type WithTrackerProxyPropsType = {
	id: number;
	title?: string;
	productId?: string;
	type: CardTypeTypes;
	activeProgramId: number;
	activeProgramExternalId: string;
	activeProgramName: string;
	numberOfDays: string;
	level: string;
	points: string;
	startDate: string;
	endDate: string;
};

export type WithTrackerPropsType = {
	trackClickShowDetail: () => void;
	trackCardCompleteAction: (type: CardTypeTypes) => void;
	trackCardProductAction: (buttonType: string) => void;
	trackCardTrackingSave: () => void;
};

const ACTION_BUTTON_TYPES = {
	COMPLETED: 'completed',
};

const COACH_CARD_TYPES = {
	PRODUCT: 'product',
};

const stringOrEmpty = (val?: string) => val || ' ';

export default function withTracker<P extends object>(WrappedComponent: React.ComponentType<P>) {
	return class TrackerProxy extends React.Component<P & WithTrackerProxyPropsType> {
		public static displayName = `withTracker(${getDisplayName(WrappedComponent)})`;

		public handleClickShowDetail = () => {
			const { type, title, activeProgramExternalId } = this.props;

			switch (type) {
				case CardTypes.Recommendation:
					dataLayerPush(
						trackProductCardOpen({
							url: getVirtualPageUrl(),
							title: stringOrEmpty(title),
							coachCardProdNm: (title as any) || '',
							coachProgId: activeProgramExternalId,
							coachCardType: 'product',
						} as any)
					);
					break;
				case CardTypes.Tracking:
					dataLayerPush(
						trackCardTrackingOpen({
							programId: activeProgramExternalId,
						})
					);

					break;
				default:
					break;
			}
		};

		public handleCardCompleteAction = (type: CardTypeTypes) => {
			const { title, productId, activeProgramExternalId } = this.props;
			dataLayerPush(
				trackCardCompletedAction({
					coachCardProdId: productId || '',
					coachCardProdNm: stringOrEmpty(title),
					coachProgId: activeProgramExternalId,
					coachCardType: type,
				} as any)
			);
		};

		public handleCardProductAction = (buttonType: string) => {
			dataLayerPush(
				trackProductCardWidgetAction({
					hearted: Boolean(buttonType === ACTION_BUTTON_TYPES.COMPLETED),
					coachCardProgId: this.props.activeProgramExternalId,
					coachCardProdId: typeof this.props.productId === 'string' ? this.props.productId.toString() : '',
					coachProgId: this.props.activeProgramExternalId,
					coachCardProdNm: stringOrEmpty(this.props.title),
					coachCardType: COACH_CARD_TYPES.PRODUCT,
				} as any)
			);
		};

		public handleCardTrackingSave = () => {
			if (this.props.activeProgramId && this.props.activeProgramName) {
				dataLayerPush(
					trackCardTrackingComplete({
						programId: this.props.activeProgramExternalId,
						coachCardProdNm: stringOrEmpty(this.props.title),
					})
				);
			}
		};

		public render() {
			return (
				<WrappedComponent
					trackClickShowDetail={this.handleClickShowDetail}
					trackCardCompleteAction={this.handleCardCompleteAction}
					trackCardProductAction={this.handleCardProductAction}
					trackCardTrackingSave={this.handleCardTrackingSave}
					{...this.props}
				/>
			);
		}
	};
}
